<script setup>
import { computed, ref } from "vue";
import { Link, router, usePage } from "@inertiajs/vue3";
import ApplicationMark from "@/Components/ApplicationMark.vue";
import Banner from "@/Components/Banner.vue";
import Dropdown from "@/Components/Dropdown.vue";
import DropdownLink from "@/Components/DropdownLink.vue";
import NavLink from "@/Components/NavLink.vue";
import ResponsiveNavLink from "@/Components/ResponsiveNavLink.vue";
import CreateNewEvent from "@/Components/CreateNewEvent.vue";
import CreateNewStage from "@/Components/CreateNewStage.vue";
import StagesHeader from "../Components/StagesHeader.vue";
import { Sortable } from "sortablejs-vue3";

const showingNavigationDropdown = ref(false);
const addingNewEvent = ref(false);
const addingNewStage = ref(false);

const logout = () => {
    router.post(route("logout"));
};

const page = usePage();
const events = computed(
    () => page.props.tenant.public_events || page.props.tenant.events
);

function updateSort(e) {
    const updatedOrder = events.value;
    const movedItem = updatedOrder.splice(e.oldIndex, 1)[0];
    updatedOrder.splice(e.newIndex, 0, movedItem);

    const dataToUpdate = updatedOrder.map((item, idx) => {
        return {
            id: item.id,
            order: idx + 1,
        };
    });

    router.post(
        route("events.sort"),
        {
            events: dataToUpdate,
        },
        {
            preserveScroll: true,
            preserveState: true,
        }
    );
}
</script>

<template>
    <div>
        <div class="flex flex-col min-h-screen">
            <div class="sticky top-0 z-50 md:relative">
                <Banner />
                <nav class="border-b border-gray-700 bg-dark">
                    <!-- Primary Navigation Menu -->
                    <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                        <div class="flex justify-between h-16">
                            <div class="flex">
                                <!-- Logo -->
                                <div class="flex items-center shrink-0">
                                    <Link :href="route('dashboard')">
                                        <ApplicationMark
                                            class="block w-auto h-10"
                                        />
                                    </Link>
                                    <!-- <div class="ml-4 text-lg font-bold lg:hidden">
                                    {{ $page.props.event?.name }}
                                </div> -->
                                </div>

                                <!-- Navigation Links -->
                                <Sortable
                                    :list="events"
                                    item-key="id"
                                    tag="div"
                                    :options="{
                                        group: 'groups',
                                        animation: 150,
                                        handle: '.event-handle',
                                        dragClass: 'opacity-0',
                                    }"
                                    class="hidden space-x-2 md:-my-px md:ms-10 md:flex"
                                    @update="updateSort"
                                >
                                    <template
                                        #item="{ element: event, index: idx }"
                                    >
                                        <NavLink
                                            :href="
                                                route('events.show', event.slug)
                                            "
                                            :disabled="page.props.admin.mode"
                                            :active="
                                                route().params?.event ===
                                                event.slug
                                            "
                                        >
                                            <template
                                                v-if="!page.props.admin.mode"
                                                >{{ event.name }}</template
                                            >
                                            <div
                                                v-else
                                                class="flex px-2 py-1 bg-gray-700 rounded-md"
                                            >
                                                <button
                                                    type="button"
                                                    @click.prevent.stop
                                                    class="inline-flex items-center mr-2 text-sm font-medium leading-4 text-gray-400 transition duration-150 ease-in-out border border-transparent rounded-md event-handle hover:text-gray-300 focus:outline-none"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                        class="size-3"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75ZM2 10a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Zm0 5.25a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z"
                                                            clip-rule="evenodd"
                                                        />
                                                    </svg>
                                                </button>
                                                {{ event.name }}
                                                <Link
                                                    as="button"
                                                    type="button"
                                                    @click.prevent.stop
                                                    :href="
                                                        route(
                                                            'events.settings',
                                                            event.slug
                                                        )
                                                    "
                                                    class="inline-flex items-center ml-2 text-sm font-medium leading-4 text-gray-400 transition duration-150 ease-in-out border border-transparent rounded-md hover:text-gray-300 focus:outline-none"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                        class="size-3"
                                                    >
                                                        <path
                                                            d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z"
                                                        />
                                                        <path
                                                            d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z"
                                                        />
                                                    </svg>
                                                </Link>
                                            </div>
                                        </NavLink>
                                    </template>
                                </Sortable>
                                <div
                                    class="inline-flex items-center px-1 pt-1 text-sm font-medium leading-5 text-gray-400 transition duration-150 ease-in-out border-b-2 border-transparent focus:outline-none"
                                >
                                    <button
                                        type="button"
                                        @click="addingNewEvent = true"
                                        v-if="$page.props.admin.mode"
                                        class="inline-flex items-center justify-center p-1 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-400 bg-dark hover:text-gray-300 focus:outline-none focus:bg-gray-700 active:bg-gray-700 transition ease-in-out duration-150 size-[28px]"
                                    >
                                        <svg
                                            class="size-4"
                                            data-slot="icon"
                                            fill="none"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                            aria-hidden="true"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M12 4.5v15m7.5-7.5h-15"
                                            ></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div class="hidden md:flex md:items-center md:ms-6">
                                <template v-if="$page.props.auth.user?.admin">
                                    <Link
                                        type="button"
                                        as="button"
                                        :href="route('admin.toggle')"
                                        method="POST"
                                        class="inline-flex items-center p-1 text-sm font-medium leading-4 transition duration-150 ease-in-out border rounded-md focus:outline-none"
                                        :class="
                                            $page.props.admin.mode
                                                ? 'bg-red-600/40 border-transparent text-red-300 hover:text-red-200 hover:bg-red-600/60'
                                                : 'border-transparent text-gray-400 bg-dark hover:text-gray-300 focus:bg-gray-700 active:bg-gray-700'
                                        "
                                    >
                                        <svg
                                            class="w-5 h-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z"
                                            />
                                        </svg>
                                        <span
                                            class="ml-1"
                                            v-if="$page.props.admin.mode"
                                            >Admin Mode Enabled</span
                                        >
                                    </Link>
                                </template>

                                <!-- Settings Dropdown -->
                                <div class="relative ms-3">
                                    <Dropdown
                                        align="right"
                                        width="48"
                                        v-if="$page.props.auth.user"
                                    >
                                        <template #trigger>
                                            <span
                                                class="inline-flex rounded-md"
                                            >
                                                <button
                                                    type="button"
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-gray-400 transition duration-150 ease-in-out border border-transparent rounded-md bg-dark hover:text-gray-300 focus:outline-none focus:bg-gray-700 active:bg-gray-700"
                                                >
                                                    <img
                                                        class="object-cover w-8 h-8 mr-2 rounded-full"
                                                        :src="
                                                            $page.props.auth
                                                                .user.avatar
                                                        "
                                                        :alt="
                                                            $page.props.auth
                                                                .user.username
                                                        "
                                                    />

                                                    {{
                                                        $page.props.auth.user
                                                            .username
                                                    }}

                                                    <svg
                                                        class="ms-2 -me-0.5 h-4 w-4"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                                        />
                                                    </svg>
                                                </button>
                                            </span>
                                        </template>

                                        <template #content>
                                            <!-- Account Management -->
                                            <div
                                                class="block px-4 py-2 text-xs text-gray-400"
                                            >
                                                Manage Account
                                            </div>

                                            <DropdownLink
                                                :href="route('profile.show')"
                                            >
                                                Profile
                                            </DropdownLink>

                                            <div
                                                class="border-t border-gray-600"
                                            />

                                            <!-- Authentication -->
                                            <form @submit.prevent="logout">
                                                <DropdownLink as="button">
                                                    Log Out
                                                </DropdownLink>
                                            </form>
                                        </template>
                                    </Dropdown>
                                    <a
                                        :href="route('login')"
                                        v-else
                                        class="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-gray-400 transition duration-150 ease-in-out border border-transparent rounded-md bg-dark hover:text-gray-300 focus:outline-none focus:bg-gray-700 active:bg-gray-700"
                                    >
                                        Log In / Register
                                    </a>
                                </div>
                            </div>

                            <!-- Hamburger -->
                            <div class="flex items-center -me-2 md:hidden">
                                <button
                                    class="inline-flex items-center justify-center p-2 text-gray-300 transition duration-150 ease-in-out rounded-md hover:text-gray-200 hover:bg-gray-900 focus:outline-none focus:bg-gray-900 focus:text-gray-200"
                                    @click="
                                        showingNavigationDropdown =
                                            !showingNavigationDropdown
                                    "
                                >
                                    <svg
                                        class="w-6 h-6"
                                        stroke="currentColor"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            :class="{
                                                hidden: showingNavigationDropdown,
                                                'inline-flex':
                                                    !showingNavigationDropdown,
                                            }"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M4 6h16M4 12h16M4 18h16"
                                        />
                                        <path
                                            :class="{
                                                hidden: !showingNavigationDropdown,
                                                'inline-flex':
                                                    showingNavigationDropdown,
                                            }"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Responsive Navigation Menu -->
                    <div
                        :class="{
                            block: showingNavigationDropdown,
                            hidden: !showingNavigationDropdown,
                        }"
                        class="md:hidden"
                    >
                        <div class="pt-2 pb-3 space-y-1">
                            <ResponsiveNavLink
                                v-for="event in events"
                                :href="route('events.show', event.slug)"
                                :active="route().params.event === event.slug"
                            >
                                {{ event.name }}
                            </ResponsiveNavLink>
                        </div>

                        <!-- Responsive Settings Options -->
                        <div
                            class="pt-4 pb-1 border-t border-gray-600"
                            v-if="$page.props.auth.user"
                        >
                            <div class="flex items-center px-4">
                                <div class="shrink-0 me-3">
                                    <img
                                        class="object-cover w-10 h-10 rounded-full"
                                        :src="$page.props.auth.user.avatar"
                                        :alt="$page.props.auth.user.name"
                                    />
                                </div>

                                <div>
                                    <div
                                        class="text-base font-medium text-gray-200"
                                    >
                                        {{ $page.props.auth.user.username }}
                                    </div>
                                    <div
                                        class="text-sm font-medium text-gray-500"
                                    >
                                        {{ $page.props.auth.user.email }}
                                    </div>
                                </div>
                            </div>

                            <div class="mt-3 space-y-1">
                                <ResponsiveNavLink
                                    :href="route('profile.show')"
                                    :active="route().current('profile.show')"
                                >
                                    Profile
                                </ResponsiveNavLink>

                                <!-- Authentication -->
                                <form method="POST" @submit.prevent="logout">
                                    <ResponsiveNavLink as="button">
                                        Log Out
                                    </ResponsiveNavLink>
                                </form>
                            </div>
                        </div>
                        <div class="border-t border-gray-600" v-else>
                            <div>
                                <ResponsiveNavLink :href="route('login')">
                                    Log In / Register
                                </ResponsiveNavLink>
                            </div>
                        </div>
                    </div>
                </nav>

                <!-- Page Raw Heading -->
                <header
                    v-if="$page.props.event"
                    class="shadow bg-dark md:block"
                    :class="showingNavigationDropdown ? 'block' : 'hidden'"
                >
                    <div class="md:border-b md:border-t md:border-dark">
                        <nav
                            class="mx-auto max-w-7xl md:px-8 lg:flex"
                            aria-label="Progress"
                        >
                            <div class="flex justify-around py-2">
                                <Link
                                    class="relative flex items-center lg:pr-8 group"
                                    :href="
                                        route(
                                            'events.show',
                                            $page.props.event.slug
                                        )
                                    "
                                >
                                    <!--                            <span class="absolute top-0 left-0 w-1 h-full bg-transparent lg:bottom-0 lg:top-auto lg:h-1 lg:w-full" aria-hidden="true"-->
                                    <!--                                  :class="route().current('events.show', $page.props.event.slug) ? 'sm:bg-primary' : 'group-hover:bg-gray-500'"/>-->
                                    <img
                                        :src="$page.props.event.logo_url"
                                        :alt="$page.props.event.name"
                                        class="w-12 h-12"
                                        draggable="false"
                                    />
                                </Link>
                                <Link
                                    class="relative flex items-center lg:hidden lg:mr-4 lg:px-4 group"
                                    :href="
                                        route(
                                            'events.standings',
                                            $page.props.event.slug
                                        )
                                    "
                                >
                                    <!--                            <span class="absolute top-0 left-0 w-1 h-full bg-transparent lg:bottom-0 lg:top-auto lg:h-1 lg:w-full" aria-hidden="true"-->
                                    <!--                                  :class="route().current('events.standings', $page.props.event.slug) ? 'sm:bg-primary' : 'group-hover:bg-gray-500'"/>-->
                                    <div
                                        class="absolute inset-0 top-0 left-0 hidden w-3 lg:block"
                                        aria-hidden="true"
                                    >
                                        <svg
                                            class="w-full h-full text-gray-700"
                                            viewBox="0 0 12 82"
                                            fill="none"
                                            preserveAspectRatio="none"
                                        >
                                            <path
                                                d="M0.5 0V31L10.5 41L0.5 51V82"
                                                stroke="currentcolor"
                                                vector-effect="non-scaling-stroke"
                                            />
                                        </svg>
                                    </div>
                                    <div class="text-white lg:pl-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            xmlns:xlink="http://www.w3.org/1999/xlink"
                                            version="1.1"
                                            x="0px"
                                            y="0px"
                                            class="size-12"
                                            viewBox="0 0 1000 1000"
                                            enable-background="new 0 0 1000 1000"
                                            xml:space="preserve"
                                        >
                                            <g>
                                                <path
                                                    fill="currentColor"
                                                    d="M763.9,177.4c-9.9,82.9-29,161.7-55.2,227.8c-1.1,2.9-0.3,6.1,2,8.1c1.3,1.2,3,1.7,4.7,1.7   c1.3,0,2.5-0.3,3.6-1c22.3-13,42.4-34,59.7-62.2c14.3-23.3,25.7-50.1,34.1-79.7c8.3-29.4,13.3-60.9,14.9-93.6c0.1-2-0.6-3.9-2-5.3   c-1.4-1.4-3.3-2.2-5.2-2.2h-49.5C767.4,171.1,764.3,173.8,763.9,177.4z M812.9,185.5c-4,60.2-20.4,116.4-46.4,158.9   c-10.9,17.8-22.9,32.4-35.8,43.7c21.7-60.5,37.7-129.9,46.8-202.5H812.9z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M868.7,130.8c-1.4-1.4-3.2-2.1-5.1-2.1h-80.4c1.1-14.8,2-29.7,2.5-44.3c0.1-2-0.7-3.9-2-5.3   c-1.4-1.4-3.2-2.2-5.2-2.2c-9.5,0-18.4-3.8-24.9-10.7c-6.5-6.8-9.9-15.8-9.5-25.2c0.1-2-0.6-3.9-2-5.3c-1.4-1.4-3.2-2.2-5.2-2.2   H262c-2,0-3.8,0.8-5.2,2.2s-2.1,3.3-2,5.3c0.4,9.4-3,18.4-9.5,25.2c-6.6,6.9-15.4,10.6-24.9,10.7c-2,0-3.8,0.8-5.2,2.2   c-1.4,1.4-2.1,3.3-2,5.3c0.6,14.6,1.4,29.5,2.5,44.3h-59.2c0,0,0,0,0,0h-20c-1.9,0-3.8,0.8-5.1,2.1c-1.4,1.4-2.1,3.2-2.1,5.1   l0.1,21.3c0.2,81.6,20,158.6,55.8,216.9c16,26,34.4,47.4,54.9,63.4c19.6,15.4,40.7,25.6,62.8,30.5c22.7,43.7,48.4,79.3,76.7,105.8   c28.2,26.5,58.3,43.3,89.4,50c-4,73.6-17.9,141.4-40.3,196.5c-22.2,54.5-51.7,92.7-83.1,107.5c-2.5,1.2-4.2,3.7-4.2,6.5v25   c0,4,3.2,7.2,7.2,7.2h299c4,0,7.2-3.2,7.2-7.2v-25c0-2.8-1.6-5.3-4.2-6.5c-31.4-14.8-60.9-52.9-83-107.3   c-22.4-55-36.3-122.6-40.4-196c31.6-6.1,62.2-22.7,91-49.3c28.7-26.6,55-62.6,78-107c22.3-4.7,43.7-14.9,63.5-30.3   c20.7-16.1,39.4-37.6,55.5-63.9c35.7-58.3,55.5-135.4,55.8-216.9l0.1-21.3C870.8,134,870,132.1,868.7,130.8z M255.7,76.2   c7.5-7.8,12.1-17.7,13.3-28.3h21.7l-61.8,61.8c-0.4-6.4-0.7-12.7-1-19.1C238.5,89.1,248.2,84,255.7,76.2z M221.3,185.5   c9,72,24.9,140.8,46.3,201c-3.1-2.8-6.2-5.8-9.2-9.1c-0.3-1-0.9-1.9-1.7-2.7c-0.6-0.6-1.3-1.1-2-1.4c-7-7.9-13.6-17-19.9-27   c-0.2-1.5-0.9-3-2-4.1c-0.3-0.3-0.6-0.6-0.9-0.8c-5.8-9.8-11.2-20.4-16-31.5c0-1.9-0.7-3.8-2.1-5.2c0,0-0.1-0.1-0.1-0.1   c-4.7-11.4-8.8-23.4-12.3-35.8c0.3-2-0.2-4-1.6-5.6c-3.4-12.9-6.3-26.4-8.4-40.1c0.6-2,0.3-4.2-0.9-6c-1.5-10.4-2.6-20.9-3.3-31.5   H221.3z M205.7,379.4l20.1-20.1c4.9,7.5,10.1,14.4,15.4,20.7l-19.8,19.8C216,393.5,210.8,386.7,205.7,379.4z M168,304.2l22.1-22.1   c2.9,9.2,6,18,9.5,26.6L178.2,330C174.5,321.6,171.1,313,168,304.2z M155.3,260.3l23.6-23.6c1.8,10,4,19.9,6.6,29.5l-22.6,22.6   C160.1,279.5,157.5,270,155.3,260.3z M176.1,219.1l-24.3,24.3c-1.9-10.3-3.5-20.7-4.8-31.3l25.7-25.7   C173.4,197.5,174.5,208.4,176.1,219.1z M145.2,193.5c-0.9-11.4-1.4-23-1.4-34.7l15.7-15.7h36.1L145.2,193.5z M184.8,343.9   l20.9-20.9c3.8,8.3,7.9,16.2,12.3,23.7l-20.4,20.4c-0.1-0.2-0.2-0.3-0.3-0.5C192.9,359.3,188.7,351.7,184.8,343.9z M231.2,410.5   l19.8-19.8c6.2,6.3,12.7,11.9,19.4,16.7l-20,20C243.8,422.4,237.4,416.7,231.2,410.5z M369.6,930l-14,14v-5.2   C360.3,936.3,365,933.4,369.6,930z M856.3,157.1c-0.2,79-19.3,153.3-53.6,209.4c-15.2,24.8-32.7,45-52,60   c-19,14.8-39.5,24.3-60.8,28.2c-2.2,0.4-4.1,1.8-5.1,3.8c-22.6,44.3-48.4,80-76.6,106c-28.5,26.4-58.8,42.1-89.8,46.7   c-3.7,0.5-6.3,3.8-6.2,7.5c7.4,156,58.6,283.5,127.9,320v13.3H481l17.6-17.6c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0   l-27.8,27.8h-36.1l74.1-74.1c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0L404,952.1h-36.1l130.6-130.6c2.8-2.8,2.8-7.4,0-10.2   c-2.8-2.8-7.4-2.8-10.2,0l-86.3,86.3c16.1-20.9,30.5-47.5,42.5-78.5l54-54c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-33,33   c4.8-15.5,9.1-31.8,12.7-48.8l30.4-30.4c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-15.2,15.2c2.4-13.7,4.5-27.8,6.1-42.2   l19.4-19.4c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-6.9,6.9c0.8-9.9,1.5-20,2-30.2c0.1-2.2-0.8-4.2-2.3-5.6l17.3-17.3   c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-23,23c-8.7-2.3-17.4-5.6-26-9.7c6.8-6.6,13.5-13.5,20.1-20.2   c3.6-3.7,7.3-7.6,11-11.2c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-3.7,3.7-7.4,7.6-11.1,11.3   c-7.5,7.7-15.2,15.7-23.1,23.1c-7.3-4.3-14.5-9.3-21.6-14.9c11.7-11.9,23.8-23.8,35.4-35.4l4.6-4.6c2.8-2.8,2.9-7.4,0.1-10.2   c-2.8-2.8-7.4-2.9-10.2-0.1l-4.6,4.6c-12,11.8-24.3,24-36.3,36.3c-1.4-1.2-2.7-2.4-4-3.7c-4.8-4.5-9.4-9.2-14-14.2   c9.3-9.4,18.8-18.7,28.1-27.7c6.4-6.2,13-12.7,19.4-19.1c2.8-2.8,2.9-7.4,0-10.2c-2.8-2.8-7.4-2.9-10.2,0c-6.4,6.4-13,12.8-19.3,19   c-9.1,8.9-18.4,18-27.5,27.2c-5.4-6.4-10.6-13.2-15.7-20.3c10-10,20.1-20,30-29.8c8.2-8.1,16.6-16.4,24.9-24.7   c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-8.3,8.2-16.7,16.6-24.9,24.7c-9.2,9.2-18.8,18.6-28.2,28   c-4.7-7.1-9.4-14.6-13.9-22.3c20.4-21,41.1-41.9,61.6-62.2c2.8-2.8,2.9-7.4,0.1-10.2c-2.8-2.8-7.4-2.9-10.2-0.1   c-19.6,19.4-39.3,39.3-58.9,59.3c-2.1-3.9-4.2-7.9-6.3-12c-1-2-2.9-3.4-5-3.8c-2.3-0.5-4.6-1-6.9-1.6c16.1-15.8,32-32,47.5-47.8   c9.1-9.2,18.4-18.8,27.7-28.1c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-9.3,9.4-18.7,18.9-27.7,28.2   c-17.1,17.5-34.9,35.6-52.7,52.9c-8.2-3.2-16.2-7.3-24-12.2l21.6-21.6c1.3-0.2,2.5-0.7,3.6-1.6c1.4-1.1,2.2-2.7,2.5-4.4   c25.5-25.1,51-50.5,76-75.6c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-23.2,23.3-46.9,46.9-70.6,70.3   c-3.3-8.7-6.4-17.6-9.4-26.7c25.8-25.3,51.6-51.3,76.6-76.5l4.3-4.3c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-4.3,4.3   c-23.3,23.5-47.3,47.6-71.3,71.3c-2.8-9.2-5.6-18.6-8.1-28.1c28.6-28.5,57.6-57.5,86.2-86.2c2.8-2.8,2.8-7.4,0-10.2   c-2.8-2.8-7.4-2.8-10.2,0c-26.7,26.7-53.6,53.6-80.3,80.2c-2.4-9.6-4.7-19.4-6.9-29.3c25.4-26.2,51.8-52.2,77.4-77.4   c4.5-4.4,9-8.9,13.5-13.3c2.8-2.8,2.9-7.4,0.1-10.2c-2.8-2.8-7.4-2.9-10.2-0.1c-4.5,4.4-9,8.9-13.5,13.3   c-23.4,23-47.4,46.7-70.8,70.7c-2-10-3.9-20.1-5.6-30.3c21.2-21.4,43-42.9,64-63.8c10.4-10.3,21.2-21,31.8-31.6   c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-10.6,10.5-21.4,21.2-31.8,31.5c-18.6,18.4-37.8,37.4-56.7,56.4   c-1.6-10.5-3-21.1-4.3-31.7c12.7-12.7,25.7-25.6,38.3-38.2c20.6-20.4,41.9-41.5,62.7-62.8c2.8-2.9,2.7-7.4-0.1-10.2   c-2.9-2.8-7.4-2.7-10.2,0.1c-20.7,21.2-41.9,42.3-62.5,62.6c-13,12.9-26.3,26.1-39.3,39.2H188l28-28h7.5c2,0,3.9-0.8,5.3-2.3   c1.4-1.5,2.1-3.5,1.9-5.5c-0.2-2.1-0.3-4.3-0.5-6.4l81-81h418.6c1.2,10.6,5.8,20.4,13.3,28.3c7.6,7.9,17.2,12.9,27.8,14.5   c-0.6,14.8-1.6,29.7-2.8,44.6c-0.2,2,0.5,4,1.9,5.5c1.4,1.5,3.3,2.3,5.3,2.3h81L856.3,157.1z"
                                                />
                                            </g>
                                        </svg>
                                    </div>
                                </Link>
                            </div>
                            <StagesHeader
                                @close-menu="showingNavigationDropdown = false"
                            />
                            <div
                                v-if="$page.props.admin.mode"
                                class="flex items-center px-1"
                            >
                                <button
                                    type="button"
                                    @click="addingNewStage = true"
                                    class="inline-flex items-center p-1 text-sm font-medium leading-4 text-gray-400 transition duration-150 ease-in-out border border-transparent rounded-md bg-dark hover:text-gray-300 focus:outline-none focus:bg-gray-700 active:bg-gray-700"
                                >
                                    <svg
                                        class="w-5 h-5"
                                        data-slot="icon"
                                        fill="none"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M12 4.5v15m7.5-7.5h-15"
                                        ></path>
                                    </svg>
                                </button>
                            </div>
                            <Link
                                class="relative items-center hidden px-4 mr-4 lg:flex group"
                                :href="
                                    route(
                                        'events.standings',
                                        $page.props.event.slug
                                    )
                                "
                            >
                                <!--                            <span class="absolute top-0 left-0 w-1 h-full bg-transparent lg:bottom-0 lg:top-auto lg:h-1 lg:w-full" aria-hidden="true"-->
                                <!--                                  :class="route().current('events.standings', $page.props.event.slug) ? 'sm:bg-primary' : 'group-hover:bg-gray-500'"/>-->
                                <div
                                    class="absolute inset-0 top-0 left-0 hidden w-3 lg:block"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="w-full h-full text-gray-700"
                                        viewBox="0 0 12 82"
                                        fill="none"
                                        preserveAspectRatio="none"
                                    >
                                        <path
                                            d="M0.5 0V31L10.5 41L0.5 51V82"
                                            stroke="currentcolor"
                                            vector-effect="non-scaling-stroke"
                                        />
                                    </svg>
                                </div>
                                <div class="text-white lg:pl-2">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        xmlns:xlink="http://www.w3.org/1999/xlink"
                                        version="1.1"
                                        x="0px"
                                        y="0px"
                                        class="size-12"
                                        viewBox="0 0 1000 1000"
                                        enable-background="new 0 0 1000 1000"
                                        xml:space="preserve"
                                    >
                                        <g>
                                            <path
                                                fill="currentColor"
                                                d="M763.9,177.4c-9.9,82.9-29,161.7-55.2,227.8c-1.1,2.9-0.3,6.1,2,8.1c1.3,1.2,3,1.7,4.7,1.7   c1.3,0,2.5-0.3,3.6-1c22.3-13,42.4-34,59.7-62.2c14.3-23.3,25.7-50.1,34.1-79.7c8.3-29.4,13.3-60.9,14.9-93.6c0.1-2-0.6-3.9-2-5.3   c-1.4-1.4-3.3-2.2-5.2-2.2h-49.5C767.4,171.1,764.3,173.8,763.9,177.4z M812.9,185.5c-4,60.2-20.4,116.4-46.4,158.9   c-10.9,17.8-22.9,32.4-35.8,43.7c21.7-60.5,37.7-129.9,46.8-202.5H812.9z"
                                            />
                                            <path
                                                fill="currentColor"
                                                d="M868.7,130.8c-1.4-1.4-3.2-2.1-5.1-2.1h-80.4c1.1-14.8,2-29.7,2.5-44.3c0.1-2-0.7-3.9-2-5.3   c-1.4-1.4-3.2-2.2-5.2-2.2c-9.5,0-18.4-3.8-24.9-10.7c-6.5-6.8-9.9-15.8-9.5-25.2c0.1-2-0.6-3.9-2-5.3c-1.4-1.4-3.2-2.2-5.2-2.2   H262c-2,0-3.8,0.8-5.2,2.2s-2.1,3.3-2,5.3c0.4,9.4-3,18.4-9.5,25.2c-6.6,6.9-15.4,10.6-24.9,10.7c-2,0-3.8,0.8-5.2,2.2   c-1.4,1.4-2.1,3.3-2,5.3c0.6,14.6,1.4,29.5,2.5,44.3h-59.2c0,0,0,0,0,0h-20c-1.9,0-3.8,0.8-5.1,2.1c-1.4,1.4-2.1,3.2-2.1,5.1   l0.1,21.3c0.2,81.6,20,158.6,55.8,216.9c16,26,34.4,47.4,54.9,63.4c19.6,15.4,40.7,25.6,62.8,30.5c22.7,43.7,48.4,79.3,76.7,105.8   c28.2,26.5,58.3,43.3,89.4,50c-4,73.6-17.9,141.4-40.3,196.5c-22.2,54.5-51.7,92.7-83.1,107.5c-2.5,1.2-4.2,3.7-4.2,6.5v25   c0,4,3.2,7.2,7.2,7.2h299c4,0,7.2-3.2,7.2-7.2v-25c0-2.8-1.6-5.3-4.2-6.5c-31.4-14.8-60.9-52.9-83-107.3   c-22.4-55-36.3-122.6-40.4-196c31.6-6.1,62.2-22.7,91-49.3c28.7-26.6,55-62.6,78-107c22.3-4.7,43.7-14.9,63.5-30.3   c20.7-16.1,39.4-37.6,55.5-63.9c35.7-58.3,55.5-135.4,55.8-216.9l0.1-21.3C870.8,134,870,132.1,868.7,130.8z M255.7,76.2   c7.5-7.8,12.1-17.7,13.3-28.3h21.7l-61.8,61.8c-0.4-6.4-0.7-12.7-1-19.1C238.5,89.1,248.2,84,255.7,76.2z M221.3,185.5   c9,72,24.9,140.8,46.3,201c-3.1-2.8-6.2-5.8-9.2-9.1c-0.3-1-0.9-1.9-1.7-2.7c-0.6-0.6-1.3-1.1-2-1.4c-7-7.9-13.6-17-19.9-27   c-0.2-1.5-0.9-3-2-4.1c-0.3-0.3-0.6-0.6-0.9-0.8c-5.8-9.8-11.2-20.4-16-31.5c0-1.9-0.7-3.8-2.1-5.2c0,0-0.1-0.1-0.1-0.1   c-4.7-11.4-8.8-23.4-12.3-35.8c0.3-2-0.2-4-1.6-5.6c-3.4-12.9-6.3-26.4-8.4-40.1c0.6-2,0.3-4.2-0.9-6c-1.5-10.4-2.6-20.9-3.3-31.5   H221.3z M205.7,379.4l20.1-20.1c4.9,7.5,10.1,14.4,15.4,20.7l-19.8,19.8C216,393.5,210.8,386.7,205.7,379.4z M168,304.2l22.1-22.1   c2.9,9.2,6,18,9.5,26.6L178.2,330C174.5,321.6,171.1,313,168,304.2z M155.3,260.3l23.6-23.6c1.8,10,4,19.9,6.6,29.5l-22.6,22.6   C160.1,279.5,157.5,270,155.3,260.3z M176.1,219.1l-24.3,24.3c-1.9-10.3-3.5-20.7-4.8-31.3l25.7-25.7   C173.4,197.5,174.5,208.4,176.1,219.1z M145.2,193.5c-0.9-11.4-1.4-23-1.4-34.7l15.7-15.7h36.1L145.2,193.5z M184.8,343.9   l20.9-20.9c3.8,8.3,7.9,16.2,12.3,23.7l-20.4,20.4c-0.1-0.2-0.2-0.3-0.3-0.5C192.9,359.3,188.7,351.7,184.8,343.9z M231.2,410.5   l19.8-19.8c6.2,6.3,12.7,11.9,19.4,16.7l-20,20C243.8,422.4,237.4,416.7,231.2,410.5z M369.6,930l-14,14v-5.2   C360.3,936.3,365,933.4,369.6,930z M856.3,157.1c-0.2,79-19.3,153.3-53.6,209.4c-15.2,24.8-32.7,45-52,60   c-19,14.8-39.5,24.3-60.8,28.2c-2.2,0.4-4.1,1.8-5.1,3.8c-22.6,44.3-48.4,80-76.6,106c-28.5,26.4-58.8,42.1-89.8,46.7   c-3.7,0.5-6.3,3.8-6.2,7.5c7.4,156,58.6,283.5,127.9,320v13.3H481l17.6-17.6c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0   l-27.8,27.8h-36.1l74.1-74.1c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0L404,952.1h-36.1l130.6-130.6c2.8-2.8,2.8-7.4,0-10.2   c-2.8-2.8-7.4-2.8-10.2,0l-86.3,86.3c16.1-20.9,30.5-47.5,42.5-78.5l54-54c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-33,33   c4.8-15.5,9.1-31.8,12.7-48.8l30.4-30.4c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-15.2,15.2c2.4-13.7,4.5-27.8,6.1-42.2   l19.4-19.4c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-6.9,6.9c0.8-9.9,1.5-20,2-30.2c0.1-2.2-0.8-4.2-2.3-5.6l17.3-17.3   c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-23,23c-8.7-2.3-17.4-5.6-26-9.7c6.8-6.6,13.5-13.5,20.1-20.2   c3.6-3.7,7.3-7.6,11-11.2c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-3.7,3.7-7.4,7.6-11.1,11.3   c-7.5,7.7-15.2,15.7-23.1,23.1c-7.3-4.3-14.5-9.3-21.6-14.9c11.7-11.9,23.8-23.8,35.4-35.4l4.6-4.6c2.8-2.8,2.9-7.4,0.1-10.2   c-2.8-2.8-7.4-2.9-10.2-0.1l-4.6,4.6c-12,11.8-24.3,24-36.3,36.3c-1.4-1.2-2.7-2.4-4-3.7c-4.8-4.5-9.4-9.2-14-14.2   c9.3-9.4,18.8-18.7,28.1-27.7c6.4-6.2,13-12.7,19.4-19.1c2.8-2.8,2.9-7.4,0-10.2c-2.8-2.8-7.4-2.9-10.2,0c-6.4,6.4-13,12.8-19.3,19   c-9.1,8.9-18.4,18-27.5,27.2c-5.4-6.4-10.6-13.2-15.7-20.3c10-10,20.1-20,30-29.8c8.2-8.1,16.6-16.4,24.9-24.7   c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-8.3,8.2-16.7,16.6-24.9,24.7c-9.2,9.2-18.8,18.6-28.2,28   c-4.7-7.1-9.4-14.6-13.9-22.3c20.4-21,41.1-41.9,61.6-62.2c2.8-2.8,2.9-7.4,0.1-10.2c-2.8-2.8-7.4-2.9-10.2-0.1   c-19.6,19.4-39.3,39.3-58.9,59.3c-2.1-3.9-4.2-7.9-6.3-12c-1-2-2.9-3.4-5-3.8c-2.3-0.5-4.6-1-6.9-1.6c16.1-15.8,32-32,47.5-47.8   c9.1-9.2,18.4-18.8,27.7-28.1c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-9.3,9.4-18.7,18.9-27.7,28.2   c-17.1,17.5-34.9,35.6-52.7,52.9c-8.2-3.2-16.2-7.3-24-12.2l21.6-21.6c1.3-0.2,2.5-0.7,3.6-1.6c1.4-1.1,2.2-2.7,2.5-4.4   c25.5-25.1,51-50.5,76-75.6c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-23.2,23.3-46.9,46.9-70.6,70.3   c-3.3-8.7-6.4-17.6-9.4-26.7c25.8-25.3,51.6-51.3,76.6-76.5l4.3-4.3c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0l-4.3,4.3   c-23.3,23.5-47.3,47.6-71.3,71.3c-2.8-9.2-5.6-18.6-8.1-28.1c28.6-28.5,57.6-57.5,86.2-86.2c2.8-2.8,2.8-7.4,0-10.2   c-2.8-2.8-7.4-2.8-10.2,0c-26.7,26.7-53.6,53.6-80.3,80.2c-2.4-9.6-4.7-19.4-6.9-29.3c25.4-26.2,51.8-52.2,77.4-77.4   c4.5-4.4,9-8.9,13.5-13.3c2.8-2.8,2.9-7.4,0.1-10.2c-2.8-2.8-7.4-2.9-10.2-0.1c-4.5,4.4-9,8.9-13.5,13.3   c-23.4,23-47.4,46.7-70.8,70.7c-2-10-3.9-20.1-5.6-30.3c21.2-21.4,43-42.9,64-63.8c10.4-10.3,21.2-21,31.8-31.6   c2.8-2.8,2.8-7.4,0-10.2c-2.8-2.8-7.4-2.8-10.2,0c-10.6,10.5-21.4,21.2-31.8,31.5c-18.6,18.4-37.8,37.4-56.7,56.4   c-1.6-10.5-3-21.1-4.3-31.7c12.7-12.7,25.7-25.6,38.3-38.2c20.6-20.4,41.9-41.5,62.7-62.8c2.8-2.9,2.7-7.4-0.1-10.2   c-2.9-2.8-7.4-2.7-10.2,0.1c-20.7,21.2-41.9,42.3-62.5,62.6c-13,12.9-26.3,26.1-39.3,39.2H188l28-28h7.5c2,0,3.9-0.8,5.3-2.3   c1.4-1.5,2.1-3.5,1.9-5.5c-0.2-2.1-0.3-4.3-0.5-6.4l81-81h418.6c1.2,10.6,5.8,20.4,13.3,28.3c7.6,7.9,17.2,12.9,27.8,14.5   c-0.6,14.8-1.6,29.7-2.8,44.6c-0.2,2,0.5,4,1.9,5.5c1.4,1.5,3.3,2.3,5.3,2.3h81L856.3,157.1z"
                                            />
                                        </g>
                                    </svg>
                                </div>
                            </Link>
                        </nav>
                    </div>
                </header>
            </div>

            <!-- Page Content -->
            <main class="flex-grow overflow-hidden">
                <div class="h-full margin-auto">
                    <slot />
                </div>
            </main>
            <footer class="w-full px-4 pb-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div class="flex items-center justify-between">
                    <div class="text-sm text-white/70">
                        Crafted with 💜 by
                        <a href="https://pollie.dev" target="_blank">Pollie</a>
                    </div>
                    <div class="flex items-center justify-end">
                        <h2 class="pt-1 text-sm text-white/70">Powered By</h2>
                        <a :href="$page.props.root_url" target="_blank">
                            <img
                                src="/images/logo.png"
                                class="w-16 h-16"
                                draggable="false"
                            />
                        </a>
                    </div>
                </div>
            </footer>
        </div>

        <CreateNewEvent
            :show-modal="addingNewEvent"
            @close="addingNewEvent = false"
        />
        <CreateNewStage
            :show-modal="addingNewStage"
            @close="addingNewStage = false"
        />
    </div>
</template>
